import { useState } from 'react'
import { Menu, ThemeConfig } from '../../../types/menu'

interface TextStyleModalProps {
  isOpen: boolean
  onClose: () => void
  menu: Menu
  onChange: (updatedMenu: Menu) => void
}

export const TextStyleModal = ({ isOpen, onClose, menu, onChange }: TextStyleModalProps) => {
  const [theme, setTheme] = useState<ThemeConfig>(menu.theme)

  if (!isOpen) return null

  const updateColors = (updates: Partial<ThemeConfig['colors']>) => {
    const newTheme = {
      ...theme,
      colors: { ...theme.colors, ...updates }
    }
    setTheme(newTheme)
  }

  const saveChanges = () => {
    const updatedMenu = {
      ...menu,
      theme,
      lastModified: new Date()
    }
    onChange(updatedMenu)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-custom-dark">Edit Styles</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-900">
            <strong>Typography Inheritance:</strong> The menu widget will inherit all font styles (family, size, weight) from your website, ensuring seamless integration.
          </p>
        </div>

        <div className="space-y-6">
          {/* Layout Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Column Layout</label>
            <div className="inline-flex rounded-md border border-gray-300 bg-white w-full justify-center" role="group">
              <button
                onClick={() => {
                  // When switching to 1 column, move all sections to column1
                  const updatedSections = menu.sections.map(section => ({
                    ...section,
                    column: 'column1' as const
                  }))
                  const newTheme = {
                    ...theme
                  }
                  setTheme(newTheme)
                  onChange({
                    ...menu,
                    layout: '1col',
                    sections: updatedSections,
                    lastModified: new Date()
                  })
                }}
                className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors flex-1 justify-center ${
                  menu.layout === '1col'
                    ? 'bg-brand-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } rounded-l-md border-r border-gray-300`}
                title="1 column"
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="6" y="3" width="8" height="14" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                </svg>
              </button>
              <button
                onClick={() => {
                  const newTheme = {
                    ...theme
                  }
                  setTheme(newTheme)
                  onChange({
                    ...menu,
                    layout: '2col-left',
                    lastModified: new Date()
                  })
                }}
                className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors flex-1 justify-center ${
                  menu.layout === '2col-left'
                    ? 'bg-brand-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } border-r border-gray-300`}
                title="2 columns (60/40)"
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="3" width="10" height="14" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <rect x="14" y="3" width="4" height="14" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                </svg>
              </button>
              <button
                onClick={() => {
                  const newTheme = {
                    ...theme
                  }
                  setTheme(newTheme)
                  onChange({
                    ...menu,
                    layout: '2col',
                    lastModified: new Date()
                  })
                }}
                className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors flex-1 justify-center ${
                  menu.layout === '2col'
                    ? 'bg-brand-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } border-r border-gray-300`}
                title="2 columns (50/50)"
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="6" height="14" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <rect x="11" y="3" width="6" height="14" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                </svg>
              </button>
              <button
                onClick={() => {
                  const newTheme = {
                    ...theme
                  }
                  setTheme(newTheme)
                  onChange({
                    ...menu,
                    layout: '2col-right',
                    lastModified: new Date()
                  })
                }}
                className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors flex-1 justify-center ${
                  menu.layout === '2col-right'
                    ? 'bg-brand-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } rounded-r-md`}
                title="2 columns (40/60)"
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="3" width="4" height="14" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <rect x="8" y="3" width="10" height="14" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Text Color Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Text Colour</label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Section Title</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={theme.colors.sectionTitle || theme.colors.primary}
                    onChange={(e) => updateColors({ sectionTitle: e.target.value })}
                    className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={theme.colors.sectionTitle || theme.colors.primary}
                    onChange={(e) => updateColors({ sectionTitle: e.target.value })}
                    className="text-xs text-gray-600 border border-gray-300 rounded px-2 py-1 w-20"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Menu Item Title</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={theme.colors.menuItemTitle || theme.colors.text}
                    onChange={(e) => updateColors({ menuItemTitle: e.target.value })}
                    className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={theme.colors.menuItemTitle || theme.colors.text}
                    onChange={(e) => updateColors({ menuItemTitle: e.target.value })}
                    className="text-xs text-gray-600 border border-gray-300 rounded px-2 py-1 w-20"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Description</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={theme.colors.description || theme.colors.secondary}
                    onChange={(e) => updateColors({ description: e.target.value })}
                    className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={theme.colors.description || theme.colors.secondary}
                    onChange={(e) => updateColors({ description: e.target.value })}
                    className="text-xs text-gray-600 border border-gray-300 rounded px-2 py-1 w-20"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Price</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={theme.colors.price || theme.colors.text}
                    onChange={(e) => updateColors({ price: e.target.value })}
                    className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={theme.colors.price || theme.colors.text}
                    onChange={(e) => updateColors({ price: e.target.value })}
                    className="text-xs text-gray-600 border border-gray-300 rounded px-2 py-1 w-20"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={saveChanges}
            className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-80 bg-brand-primary"
          >
            Save changes
          </button>
        </div>
      </div>
    </div>
  )
}

export default TextStyleModal